import React, { useState } from "react";
import emailjs from "@emailjs/browser";
import { ShieldCheck, Loader2, CheckCircle2 } from "lucide-react";
import { trackEvent } from "../utils/analytics";
import { businessInfo } from "../mock";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const PROPERTY_TYPES = [
  "Residential home",
  "Condominium / unit",
  "Condo / HOA association",
  "Vacation rental",
  "Commercial building",
  "Multifamily / apartments",
  "Hospitality (hotel/restaurant)",
  "Other",
];
const DAMAGE_TYPES = [
  "Roof",
  "Wind",
  "Water",
  "Flood",
  "Storm Surge",
  "Interior",
  "Exterior",
  "Commercial",
  "Multiple areas",
  "Unsure",
];
const YES_NO = ["Yes", "No"];

const field =
  "w-full bg-black border border-white/15 text-white px-4 py-3 text-sm focus:border-[#E11D26] focus:outline-none placeholder:text-white/40";
const label = "block text-white/70 text-xs font-bold tracking-wider uppercase mb-1.5";

// Reusable hurricane-claim lead form. Saves to the existing leads system
// (/api/hurricane/lead) and fires GA4 lead_submitted with lead_source/region.
const HurricaneLeadForm = ({ leadSource, region, page, heading = "Get Your Hurricane Claim Reviewed" }) => {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", zip_code: "",
    property_type: "", damage_type: "",
    claim_filed: "", carrier_inspected: "", payment_or_denial: "",
    website: "",
  });
  const [status, setStatus] = useState("idle");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Instant alert to Joe via the same EmailJS service/template as the contact form.
  // Lead is already saved in the DB; an email failure must not block the success state.
  const sendLeadAlert = async () => {
    const message = `HURRICANE CLAIM LEAD — ${region}
=====================================
Name: ${form.name}
Phone: ${form.phone}
Email: ${form.email}
ZIP Code: ${form.zip_code || "(not provided)"}

Property Type: ${form.property_type || "(not provided)"}
Damage Type: ${form.damage_type || "(not provided)"}
Claim Already Filed: ${form.claim_filed || "(not provided)"}
Carrier Inspected: ${form.carrier_inspected || "(not provided)"}
Payment or Denial Received: ${form.payment_or_denial || "(not provided)"}

Region: ${region}
Source: ${leadSource}
Page: ${window.location.origin}/${page}
=====================================
Hurricane Isaias lead — homeowner is expecting a prompt call back.`;
    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          from_name: form.name,
          from_email: form.email,
          reply_to: form.email,
          phone: form.phone,
          address: form.zip_code ? `ZIP ${form.zip_code} (${region})` : region,
          subject: `[HURRICANE LEAD] ${form.name} — ${region.split(" /")[0]} · ${form.damage_type || "Damage"}`,
          message,
          submitted_at: new Date().toLocaleString("en-US", { timeZone: "America/New_York" }),
          to_email: businessInfo.emailJoe,
        },
        { publicKey: EMAILJS_PUBLIC_KEY }
      );
    } catch (err) {
      console.error("Hurricane lead email alert failed", err);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (status === "loading") return;
    setStatus("loading");
    try {
      const res = await fetch(`${BACKEND_URL}/api/hurricane/lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, lead_source: leadSource, region, page }),
      });
      if (!res.ok) throw new Error("submit failed");
      await sendLeadAlert();
      trackEvent("lead_submitted", {
        lead_source: leadSource,
        region,
        damage_type: form.damage_type,
        property_type: form.property_type,
      });
      setStatus("success");
    } catch (err) {
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div
        className="bg-[#0F0F0F] border border-[#E11D26]/40 p-8 text-center"
        data-testid="hurricane-lead-success"
      >
        <CheckCircle2 className="h-10 w-10 text-[#E11D26] mx-auto" />
        <h3 className="mt-4 text-white text-xl font-black uppercase" style={{ fontFamily: "'Anton','Oswald',sans-serif" }}>
          Thank you — your request is in.
        </h3>
        <p className="mt-2 text-white/70 text-sm">
          A licensed Claim Beast public adjuster will reach out shortly to review your
          Hurricane Isaias claim. No upfront cost, no obligation.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-[#0F0F0F] border border-white/10 p-6 sm:p-8" data-testid="hurricane-lead-form">
      <div className="flex items-center gap-2 mb-5">
        <ShieldCheck className="h-5 w-5 text-[#E11D26]" />
        <h3 className="text-white text-xl font-black uppercase" style={{ fontFamily: "'Anton','Oswald',sans-serif" }}>
          {heading}
        </h3>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className={label}>Name</label>
          <input className={field} required value={form.name} onChange={set("name")} data-testid="hlf-name" placeholder="Full name" />
        </div>
        <div>
          <label className={label}>Phone</label>
          <input className={field} required value={form.phone} onChange={set("phone")} data-testid="hlf-phone" placeholder="(850) 555-0123" />
        </div>
        <div>
          <label className={label}>Email</label>
          <input className={field} type="email" required value={form.email} onChange={set("email")} data-testid="hlf-email" placeholder="you@email.com" />
        </div>
        <div>
          <label className={label}>ZIP Code</label>
          <input className={field} value={form.zip_code} onChange={set("zip_code")} data-testid="hlf-zip" placeholder="32501" />
        </div>
        <div>
          <label className={label}>Property Type</label>
          <select className={field} value={form.property_type} onChange={set("property_type")} data-testid="hlf-property">
            <option value="">Select…</option>
            {PROPERTY_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <label className={label}>Damage Type</label>
          <select className={field} value={form.damage_type} onChange={set("damage_type")} data-testid="hlf-damage">
            <option value="">Select…</option>
            {DAMAGE_TYPES.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div>
          <label className={label}>Claim already filed?</label>
          <select className={field} value={form.claim_filed} onChange={set("claim_filed")} data-testid="hlf-filed">
            <option value="">Select…</option>
            {YES_NO.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div>
          <label className={label}>Carrier inspected?</label>
          <select className={field} value={form.carrier_inspected} onChange={set("carrier_inspected")} data-testid="hlf-inspected">
            <option value="">Select…</option>
            {YES_NO.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className={label}>Payment or denial received?</label>
          <select className={field} value={form.payment_or_denial} onChange={set("payment_or_denial")} data-testid="hlf-outcome">
            <option value="">Select…</option>
            <option value="Payment received">Payment received</option>
            <option value="Denial received">Denial received</option>
            <option value="Nothing yet">Nothing yet</option>
          </select>
        </div>
      </div>
      {/* Honeypot */}
      <input type="text" tabIndex="-1" autoComplete="off" value={form.website} onChange={set("website")} className="hidden" aria-hidden="true" />
      {status === "error" && (
        <p className="mt-4 text-[#E11D26] text-sm" data-testid="hlf-error">
          Something went wrong. Please call us or try again.
        </p>
      )}
      <button
        type="submit"
        disabled={status === "loading"}
        data-testid="hlf-submit"
        className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#E11D26] hover:bg-[#c11820] disabled:opacity-60 text-white font-bold uppercase tracking-wider px-7 py-4 transition-colors"
      >
        {status === "loading" ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
        Review My Claim
      </button>
      <p className="mt-3 text-white/40 text-xs text-center">
        No upfront cost. Licensed Florida public adjusters. We only get paid when you do.
      </p>
    </form>
  );
};

export default HurricaneLeadForm;
